import { ChevronDown, ChevronRight } from '@untitledui/icons';
import { type ReactNode, useCallback, useState } from 'react';

import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import {
  RadioButton,
  RadioGroup,
} from '@/lib/base/radio-buttons/radio-buttons';
import { ExpandableMultiValueLabel } from '@/lib/crm/components/properties/labels/ExpandableMultiValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject, PropertyValue } from '@/types/crm';

import type {
  MultiPropertyResolution,
  SinglePropertyResolution,
} from './types';

export interface ConflictRowProps {
  conflict: SinglePropertyResolution;
  schema: PropertySchemaMap;
  currentWinner: 'canonical' | 'source';
  onSelectWinner: (
    propertyName: string,
    winner: 'canonical' | 'source',
    conflict: SinglePropertyResolution
  ) => void;
}

export function ConflictRow({
  conflict,
  schema,
  currentWinner,
  onSelectWinner,
}: ConflictRowProps) {
  const propSchema: PropertyTypeSchema | undefined =
    schema[conflict.propertyName];

  const handleChange = useCallback(
    (value: string) => {
      onSelectWinner(
        conflict.propertyName,
        value as 'canonical' | 'source',
        conflict
      );
    },
    [onSelectWinner, conflict]
  );

  return (
    <div
      className='mb-4 rounded-lg border border-[var(--color-border-secondary)] p-3'
      data-testid={`merge-conflict-${conflict.propertyName}`}
    >
      <div className='mb-2 text-body-small font-medium text-[var(--color-text-secondary)]'>
        {propSchema?.label ?? conflict.propertyName}
      </div>
      <RadioGroup
        aria-label={`Resolve ${conflict.propertyName}`}
        value={currentWinner}
        onChange={handleChange}
        className='gap-2'
      >
        <RadioButton
          value='canonical'
          label={
            <span className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
              <span className='text-body-small text-[var(--color-text-tertiary)]'>
                Canonical
              </span>
              <PropertyValueLabel
                value={conflict.canonicalValue?.value}
                propertyType={propSchema}
                fontSize='sm'
              />
            </span>
          }
        />
        <RadioButton
          value='source'
          label={
            <span className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
              <span className='text-body-small text-[var(--color-text-tertiary)]'>
                Source
              </span>
              <PropertyValueLabel
                value={conflict.sourceValue?.value}
                propertyType={propSchema}
                fontSize='sm'
              />
            </span>
          }
        />
      </RadioGroup>
    </div>
  );
}

export interface MultiItemCheckboxProps {
  item: { value: PropertyValue; keep: boolean; stableKey: string };
  index: number;
  side: 'canonical' | 'source';
  propSchema: PropertyTypeSchema | undefined;
  propertyName: string;
  multi: MultiPropertyResolution;
  onToggleItem: (
    propertyName: string,
    side: 'canonical' | 'source',
    index: number,
    multi: MultiPropertyResolution
  ) => void;
}

export function MultiItemCheckbox({
  item,
  index,
  side,
  propSchema,
  propertyName,
  multi,
  onToggleItem,
}: MultiItemCheckboxProps) {
  const handleChange = useCallback(() => {
    onToggleItem(propertyName, side, index, multi);
  }, [onToggleItem, propertyName, side, index, multi]);

  return (
    <Checkbox
      isSelected={item.keep}
      onChange={handleChange}
      label={
        <PropertyValueLabel
          value={item.value.value}
          propertyType={propSchema}
          fontSize='sm'
        />
      }
    />
  );
}

export interface MultiRowProps {
  multi: MultiPropertyResolution;
  schema: PropertySchemaMap;
  current: MultiPropertyResolution;
  onToggleItem: (
    propertyName: string,
    side: 'canonical' | 'source',
    index: number,
    multi: MultiPropertyResolution
  ) => void;
}

export function MultiRow({
  multi,
  schema,
  current,
  onToggleItem,
}: MultiRowProps) {
  const propSchema: PropertyTypeSchema | undefined = schema[multi.propertyName];

  return (
    <div
      className='mb-4 rounded-lg border border-[var(--color-border-secondary)] p-3'
      data-testid={`merge-multi-${multi.propertyName}`}
    >
      <div className='mb-2 text-body-small font-medium text-[var(--color-text-secondary)]'>
        {propSchema?.label ?? multi.propertyName}
      </div>
      <div className='flex flex-col gap-1'>
        {current.canonicalItems.map((item, i) => (
          <MultiItemCheckbox
            key={item.stableKey}
            item={item}
            index={i}
            side='canonical'
            propSchema={propSchema}
            propertyName={multi.propertyName}
            multi={multi}
            onToggleItem={onToggleItem}
          />
        ))}
        {current.sourceItems.map((item, i) => (
          <MultiItemCheckbox
            key={item.stableKey}
            item={item}
            index={i}
            side='source'
            propSchema={propSchema}
            propertyName={multi.propertyName}
            multi={multi}
            onToggleItem={onToggleItem}
          />
        ))}
      </div>
    </div>
  );
}

export interface CollapsibleSectionProps {
  title: string;
  count: number;
  children: ReactNode;
}

/**
 * A collapsible review section (Auto-resolved, Matching) with a count in the
 * toggle. Renders nothing when count is 0. Content is composed by the caller so
 * a single section can hold both property and association entries.
 */
export function CollapsibleSection({
  title,
  count,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  if (count === 0) {
    return null;
  }

  return (
    <div className='mb-4'>
      <Button
        color='secondary'
        onClick={handleToggle}
        aria-expanded={isOpen}
        iconLeading={isOpen ? ChevronDown : ChevronRight}
        className='w-full justify-start'
      >
        {`${title} (${count})`}
      </Button>
      {isOpen && (
        <div className='mt-2 flex flex-col gap-2 rounded-lg border border-[var(--color-border-secondary)] p-3'>
          {children}
        </div>
      )}
    </div>
  );
}

export interface PropertyValueRowProps {
  label: string;
  value: unknown;
  propertyType: PropertyTypeSchema | undefined;
}

/** A single read-only "label: value" row, used in Auto-resolved/Matching. */
export function PropertyValueRow({
  label,
  value,
  propertyType,
}: PropertyValueRowProps) {
  return (
    <div className='flex items-start gap-2'>
      <span className='w-32 shrink-0 text-body-small text-[var(--color-text-tertiary)]'>
        {label}
      </span>
      <PropertyValueLabel
        value={value}
        propertyType={propertyType}
        fontSize='sm'
      />
    </div>
  );
}

export interface MergePreviewPanelProps {
  previewObject: CrmObject | undefined;
  schema: PropertySchemaMap;
  detailOrderKeys: string[];
}

/** Resolve the display name shown for the merged preview record. */
function getPreviewDisplayName(previewObject: CrmObject | undefined): string {
  // Generic fallback — never a raw/truncated entity id.
  return previewObject ? getDisplayName(previewObject, 'Unnamed record') : '';
}

/** The first few populated detail fields, in detail-order, shown in the preview. */
function getPreviewFieldKeys(
  previewObject: CrmObject | undefined,
  detailOrderKeys: string[]
): string[] {
  return detailOrderKeys
    .filter(key => previewObject?.properties?.[key] !== undefined)
    .slice(0, 5);
}

/** Shared "label: value" field list rendered by both preview variants. */
function PreviewFieldList({
  previewObject,
  schema,
  fieldKeys,
}: {
  previewObject: CrmObject | undefined;
  schema: PropertySchemaMap;
  fieldKeys: string[];
}) {
  return (
    <div className='flex flex-col gap-2'>
      {fieldKeys.map(key => {
        const propSchema = schema[key];
        const val = previewObject?.properties?.[key];
        if (!val) {
          return null;
        }
        return (
          <div key={key} className='flex flex-col gap-0.5'>
            <span className='text-body-small text-[var(--color-text-tertiary)]'>
              {propSchema?.label ?? key}
            </span>
            <ExpandableMultiValueLabel
              value={val.value}
              propertyType={propSchema}
              fontSize='sm'
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Desktop preview sidebar (≥ md). Hidden on mobile, where
 * {@link MergePreviewCollapsible} renders the same data as a disclosure at the
 * top of the column so the fixed 288px width never crushes the conflict
 * controls on narrow screens.
 */
export function MergePreviewPanel({
  previewObject,
  schema,
  detailOrderKeys,
}: MergePreviewPanelProps) {
  const displayName = getPreviewDisplayName(previewObject);
  const displayFields = getPreviewFieldKeys(previewObject, detailOrderKeys);

  return (
    <aside className='hidden w-72 shrink-0 overflow-y-auto rounded-lg border border-[var(--color-border-secondary)] p-4 md:block'>
      <h3 className='mb-3 text-body-small font-semibold text-[var(--color-text-secondary)]'>
        Preview
      </h3>
      <div className='mb-3'>
        <div className='text-heading-4 font-semibold text-[var(--color-text-primary)]'>
          {displayName}
        </div>
      </div>
      <PreviewFieldList
        previewObject={previewObject}
        schema={schema}
        fieldKeys={displayFields}
      />
    </aside>
  );
}

/**
 * Mobile preview disclosure (< md). Renders a collapsed "Preview · {name}"
 * header at the top of the column; tapping it expands the same fields shown in
 * the desktop sidebar. Hidden on desktop, where {@link MergePreviewPanel} owns
 * the preview. Progressive disclosure keeps the preview reachable without
 * stealing vertical space from the conflict-resolution controls.
 */
export function MergePreviewCollapsible({
  previewObject,
  schema,
  detailOrderKeys,
}: MergePreviewPanelProps) {
  const displayName = getPreviewDisplayName(previewObject);
  const displayFields = getPreviewFieldKeys(previewObject, detailOrderKeys);

  return (
    <Accordion className='mb-4 shrink-0 md:hidden'>
      <AccordionItem className='rounded-lg border border-[var(--color-border-secondary)]'>
        <AccordionTrigger className='flex min-h-11 w-full items-center gap-2 px-3 py-2.5 text-left'>
          <AccordionChevron variant='leading' />
          <span className='shrink-0 text-body-small font-semibold text-[var(--color-text-secondary)]'>
            Preview
          </span>
          {displayName && (
            <span className='min-w-0 truncate text-body-small text-[var(--color-text-tertiary)]'>
              {displayName}
            </span>
          )}
        </AccordionTrigger>
        <AccordionPanel className='border-t border-[var(--color-border-secondary)] px-3 py-3'>
          <PreviewFieldList
            previewObject={previewObject}
            schema={schema}
            fieldKeys={displayFields}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
